import { Ability } from '@casl/ability';
//@ts-ignore
import { prune } from '@mindtickle/utils';

import Can from '../components/Can';

import { PERMISSION_RULES_MAP } from '../config/rules';
import { DEFAULT_CONTEXT_PERMISSION, PERMISSIONS, DEFAULT_SUBJECT_NAME } from '../config/constants';

import { valueOf, TPermissions } from '../types';

const removeDuplicates = (arr: any[]) =>
  arr.filter((value, index, self) => self.indexOf(value) === index);

export const getRulesByPermissions = (permissions: valueOf<typeof PERMISSIONS>[]) =>
  permissions.reduce((result: { subject: string; actions: string[] }[], permission) => {
    const rules = PERMISSION_RULES_MAP[permission];
    result = result.concat(rules);
    return result;
  }, []);

export function createCanBoundTo(ability: Ability, globalAbility: Ability) {
  return function BoundCan(props: any) {
    return <Can {...props} ability={ability} globalAbility={globalAbility} />;
  };
}

export const getAllowedPermissions = (
  globalPermissions: TPermissions,
  contextPermissions: TPermissions = DEFAULT_CONTEXT_PERMISSION
) => {
  const {
    ALLOW: globalAllow = [],
    DENY: globalDeny = [],
    OVERRIDE: globalOverride = [],
  } = globalPermissions;
  const { ALLOW: contextAllow = [], DENY: contextDeny = [] } = contextPermissions;

  const allowedPermissions = removeDuplicates([
    ...globalOverride,
    ...globalAllow.filter(permission => !contextDeny.includes(permission)),
    ...contextAllow.filter(permission => !globalDeny.includes(permission)),
  ]);

  const allPossiblePermissions = removeDuplicates([
    ...globalAllow,
    ...globalDeny,
    ...globalOverride,
  ]);

  return allowedPermissions.filter(permission => allPossiblePermissions.includes(permission));
};

export const getCaslAbility = (permissions: TPermissions, scopedPermissions?: TPermissions) => {
  const allowedPermissions = getAllowedPermissions(permissions, scopedPermissions);
  const rules = getRulesByPermissions(allowedPermissions);

  const ability = new Ability(prune(rules), {
    subjectName: DEFAULT_SUBJECT_NAME,
  });
  return ability;
};

export const getAbility = (permissions: TPermissions, scopedPermissions?: TPermissions) => {
  const globalAbility = getCaslAbility(permissions);
  const scopedAbility = scopedPermissions
    ? getCaslAbility(permissions, scopedPermissions)
    : globalAbility;

  return {
    ability: scopedAbility,
    globalAbility,
  };
};
